import { ResultSet } from "@cubejs-client/core";
import { AppThunk } from "appThunk";
import { cubeLoad } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";

import { DateTime } from "luxon";
import { CustomDate, getDateDimensionNames } from "modules/customer/insights/cost/customDate";

export class MonthlyCosts {
    public readonly date: DateTime;
    public readonly customDate: CustomDate;
    public readonly costType: string;
    public readonly costValue: number;
    public readonly group: string;

    constructor(
        date: DateTime,
        customDate: CustomDate,
        costType: string,
        costValue: number,
        group: string
    ) {
        this.date = date;
        this.customDate = customDate;
        this.costType = costType;
        this.costValue = costValue;
        this.group = group;
    }
}

export const loadMonthlyCosts = (costReferenceDate: DateTime, useCustomCalendar: boolean): AppThunk<Promise<MonthlyCosts[]>> => async (dispatch) => {
    try {
        if (!costReferenceDate) {
            return [];
        }

        const priorFiveYearsToReferenceDate = costReferenceDate.minus({ years: 5 }).plus({ days: 1 }).startOf("day");
        const dateDimensions = getDateDimensionNames(useCustomCalendar);

        const costQuery = {
            measures: ["F_Cost.SumCostValue"],
            timeDimensions: [{
                dimension: "D_Date.Date",
                dateRange: [priorFiveYearsToReferenceDate, costReferenceDate.endOf('day')]
            }],
            dimensions: [
                `D_Date.${dateDimensions.monthPeriodStartDate}`,
                `D_Date.${dateDimensions.monthPeriodNumber}`,
                "D_Date.QuarterNo",
                `D_Date.${dateDimensions.yearNumber}`,
                "D_Account.AccountName",
                "D_Store.Group"
            ],
            order: [[`D_Date.${dateDimensions.monthPeriodStartDate}`, "asc"]]
        };

        const resultSet = await dispatch(cubeLoad(costQuery)) as unknown as ResultSet;
        const costRawData = resultSet.rawData();

        return costRawData.map(costRow => {
            return new MonthlyCosts(
                DateTime.fromISO(costRow[`D_Date.${dateDimensions.monthPeriodStartDate}`], { zone: "utc" }),
                new CustomDate(
                    DateTime.fromISO(costRow[`D_Date.${dateDimensions.monthPeriodStartDate}`], { zone: "utc" }),
                    Number(costRow[`D_Date.${dateDimensions.monthPeriodNumber}`]),
                    Number(costRow["D_Date.QuarterNo"]),
                    Number(costRow[`D_Date.${dateDimensions.yearNumber}`]),
                ),
                costRow["D_Account.AccountName"],
                Number(costRow["F_Cost.SumCostValue"]),
                costRow["D_Store.Group"]
            );
        });
    } catch (error) {
        dispatch(logError("Error loading MonthlyCosts.", error));
        throw error;
    }
};
